import React, { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { IconButton } from '@mui/material';
import type { Element } from 'hast';
import { Copy, FilePlus2 } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';

import { useAssistantMessageHandlers } from '../../chat-hooks/useAssistantMessageHandlers';
import type { MarkdownComponentProps } from '../ChatPanelMarkdown';
import { dataIdMap } from '../remark-plugins/remark-constants';

const useStyles = makeStyles()(theme => ({
  table: {
    borderCollapse: 'collapse' as const,
    tableLayout: 'fixed' as const,
    width: '100%',
  },
  tableCell: {
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    padding: theme.spacing(1),
    verticalAlign: 'top',
    wordBreak: 'break-word' as const,
  },
  tableContainer: {
    position: 'relative' as const,
  },
  tableHead: {
    backgroundColor: theme.appColors.background,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    verticalAlign: 'center',
    wordBreak: 'break-word' as const,
  },
}));

export const TableElement = React.memo(function ChatTable(
  props: MarkdownComponentProps<HTMLTableElement>
): JSX.Element {
  const { node } = props;
  const { classes } = useStyles();
  const tableInfo = getTableInfoFromNode(node);
  const tableRef = useRef<HTMLTableElement>(null);
  const { copyMarkdownToClipboard, createNewDocumentFromMarkdown } = useAssistantMessageHandlers();
  const markdown = tableInfo?.tableMarkdown;
  const sentinelRef = useRef<HTMLDivElement>(null);
  const entry = useIntersection(sentinelRef);

  const handleCopy = () => {
    if (!markdown) {
      console.error('Table markdown is not available.');
      return;
    }
    copyMarkdownToClipboard(markdown, 'Table copied to clipboard.');
  };

  const handleCreateDocument = () => {
    if (!markdown) {
      console.error('Table markdown is not available.');
      return;
    }
    createNewDocumentFromMarkdown('Table', markdown);
  };

  return (
    <div className={classes.tableContainer}>
      <div ref={sentinelRef} aria-hidden />
      <ActionButtons
        isStuck={!entry?.isIntersecting}
        onCopy={handleCopy}
        onCreateDocument={handleCreateDocument}
      />
      <table {...props} ref={tableRef} className={classes.table} />
    </div>
  );
});

export const TableCellElement = React.memo(function ChatTableCell(
  props: MarkdownComponentProps<HTMLTableCellElement>
): JSX.Element {
  const { classes } = useStyles();
  return <td {...props} className={classes.tableCell} />;
});

export const TableHeadElement = React.memo(function ChatTableHead(
  props: MarkdownComponentProps<HTMLTableCellElement>
): JSX.Element {
  const { classes } = useStyles();
  return <th {...props} className={classes.tableHead} />;
});

const useActionButtonStyles = makeStyles<{ isStuck: boolean }>()((theme, { isStuck }) => ({
  actionButtonsToolbar: {
    alignItems: 'center',
    borderLeft: !isStuck ? `1px solid ${theme.palette.divider}` : undefined,
    borderRight: !isStuck ? `1px solid ${theme.palette.divider}` : undefined,
    borderTop: !isStuck ? `1px solid ${theme.palette.divider}` : undefined,
    boxSizing: 'border-box' as const,
    display: 'flex',
    gap: theme.spacing(0.25),
    justifyContent: 'flex-end',
    justifySelf: 'end',
    padding: theme.spacing(0.5),
    position: 'sticky' as const,
    top: 4,
    width: !isStuck ? '100%' : 'fit-content',
    /**
     * Sticky toolbar only needs to render above its own table cells.
     * Was `theme.zIndex.tooltip` (1500), which forced every adjacent
     * floating UI (artifacts bar, composer popovers, table-insert grid)
     * into a z-index arms race. A modest value clears the table content
     * and lets normal MUI layering work everywhere else.
     */
    zIndex: 2,
  },
  buttonsContainer: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: isStuck ? 8 : undefined,
    boxShadow: isStuck ? theme.shadows[2] : undefined,
    display: 'flex',
    marginRight: theme.spacing(0.5),
  },
}));

function ActionButtons({
  isStuck,
  onCopy,
  onCreateDocument,
}: {
  isStuck: boolean;
  onCopy: () => void;
  onCreateDocument: () => void;
}): JSX.Element {
  const { classes } = useActionButtonStyles({ isStuck });

  return (
    <div className={classes.actionButtonsToolbar}>
      <div className={classes.buttonsContainer}>
        <AlineTooltip arrow placement="top" title="Copy table to clipboard">
          <IconButton size="small" onClick={onCopy}>
            <Copy size={16} stroke="currentColor" strokeWidth={1.5} />
          </IconButton>
        </AlineTooltip>
        <AlineTooltip arrow placement="top" title="Create Aline document from table">
          <IconButton size="small" onClick={onCreateDocument}>
            <FilePlus2 size={16} stroke="currentColor" strokeWidth={1.5} />
          </IconButton>
        </AlineTooltip>
      </div>
    </div>
  );
}

const tableInfoSchema = z.object({
  tableMarkdown: z.string().optional(),
});

/**
 * `mdast-util-to-hast` keys data-* attributes by their camelCase property
 * name (e.g. `data-table-markdown` → `dataTableMarkdown`). The outer
 * `ChatPanelMarkdown` happens to leave both forms on the node via its
 * `rehypeStoreProperties` / `rehypeRestoreProperties` pair, but nested
 * `AlineMarkdown` instances (e.g. inside `CodeDraftElement`) skip that
 * store/restore — so only the camelCase form survives there. Read the
 * camelCase key first and fall back to the kebab form so we work in
 * either rendering context.
 */
const TABLE_MARKDOWN_HAST_KEY = dataIdMap.tableMarkdown.replace(/-([a-z])/g, (_, char: string) =>
  char.toUpperCase()
);

function getTableInfoFromNode(node: Element | undefined): { tableMarkdown?: string } | null {
  const properties = node?.properties;
  const value = properties?.[TABLE_MARKDOWN_HAST_KEY] ?? properties?.[dataIdMap.tableMarkdown];
  const result = tableInfoSchema.safeParse({ tableMarkdown: value });
  if (!result.success) return null;
  return result.data;
}

function useIntersection(ref: RefObject<HTMLElement | null>): IntersectionObserverEntry | null {
  const [intersectionObserverEntry, setIntersectionObserverEntry] =
    useState<IntersectionObserverEntry | null>(null);

  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver !== 'function') return () => {};

    function handler(entries: IntersectionObserverEntry[]) {
      setIntersectionObserverEntry(entries[0]);
    }

    const observer = new IntersectionObserver(handler, { root: null, threshold: 1 });
    observer.observe(ref.current);

    return () => {
      setIntersectionObserverEntry(null);
      observer.disconnect();
    };
  }, [ref]);

  return intersectionObserverEntry;
}
